import React from 'react';
import { useTheme } from '@veneer/core';

import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import { formatAccessRemaining } from 'src/utils/format-access-remaining';

import { AccessRemainingText } from '../styled';

export type AccessRemainingCellProps = {
  expiresAt: string | undefined;
};

const AccessRemainingCell: React.FC<AccessRemainingCellProps> = (
  props: AccessRemainingCellProps
) => {
  const { expiresAt } = props;
  const { translate } = useDependencyManagerContext();
  const theme = useTheme();

  if (!expiresAt) {
    return (
      <AccessRemainingText
        $veneerTheme={theme}
        $isExpiringSoon={false}
        $isExpired={false}
      >
        {'-'}
      </AccessRemainingText>
    );
  }

  const { daysLeft, formattedDate, isExpiringSoon, isExpired } =
    formatAccessRemaining(expiresAt);

  let displayText: string;
  if (isExpired) {
    const daysExpired = daysLeft === null ? 0 : Math.abs(daysLeft);
    switch (daysExpired) {
      case 0:
        displayText = translate(
          'components.shared-table-layout.access-remaining.expired-today'
        );
        break;
      case 1:
        displayText = translate(
          'components.shared-table-layout.access-remaining.expired-yesterday'
        );
        break;
      default:
        displayText = translate(
          'components.shared-table-layout.access-remaining.expired-days-ago',
          { count: daysExpired }
        );
    }
  } else if (isExpiringSoon && daysLeft !== null) {
    switch (daysLeft) {
      case 0:
        displayText = translate(
          'components.shared-table-layout.access-remaining.expires-today'
        );
        break;
      case 1:
        displayText = translate(
          'components.shared-table-layout.access-remaining.one-day-left',
          { count: daysLeft }
        );
        break;
      default:
        displayText = translate(
          'components.shared-table-layout.access-remaining.days-left',
          { count: daysLeft }
        );
    }
  } else {
    displayText = formattedDate;
  }

  return (
    <AccessRemainingText
      $veneerTheme={theme}
      $isExpiringSoon={isExpiringSoon}
      $isExpired={isExpired}
    >
      {displayText}
    </AccessRemainingText>
  );
};

export default AccessRemainingCell;
